import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle, ArrowRight, Envelope, CheckCircle, Clock, Gavel, Eye, PenNib,
  Buildings, SignOut, CaretRight, ShieldCheck,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GlassCard } from "@/components/glass/GlassCard";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import inboxApi, { inboxToken } from "@/lib/inboxApi";
import Brand from "@/components/Brand";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// =========== LANDING: email form + magic link ===========
function InboxLanding({ tenantBranding, tenantName, tenantSlug, onSent }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setLoading(true);
    try {
      await inboxApi.post("/external/request-link", {
        email,
        tenant_slug: tenantSlug,
        base_url: window.location.origin,
      });
      setSent(true);
      onSent?.();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not send link — try again");
    } finally { setLoading(false); }
  };

  const brand = tenantBranding?.primary_color ? { "--accent": tenantBranding.primary_color } : {};
  const logo = tenantBranding?.logo?.url;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 relative overflow-hidden" style={brand}>
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-[var(--bg)] via-[var(--bg)] to-[var(--surface)]" />
      <div className="fixed inset-0 -z-10 opacity-60">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl bg-[var(--accent)]/10" />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl bg-[var(--primary)]/10" />
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-8" data-testid="inbox-tenant-header">
          {logo ? (
            <img src={logo} alt={tenantName} className="w-10 h-10 rounded-md object-contain bg-[var(--surface)] border border-[var(--border)]" />
          ) : (
            <Brand size={40} testId="inbox-landing-brand" />
          )}
          <div>
            <div className="text-sm font-medium tracking-tight">{tenantName || "Clariva"}</div>
            <div className="overline text-[10px]">Signatory inbox</div>
          </div>
        </div>

        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-3" style={{ fontFamily: "var(--font-heading)" }}>
          Every contract awaiting your signature. In one place.
        </h1>
        <p className="text-base text-[var(--text-2)] mb-8">
          Sign in once and see contracts from every {tenantName ? tenantName + " team" : "Clariva customer"} who has invited you. No passwords, no accounts.
        </p>

        <AnimatePresence mode="wait">
          {sent ? (
            <motion.div key="sent" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <GlassCard className="p-6 text-center" data-testid="magic-link-sent">
                <CheckCircle size={32} weight="fill" className="mx-auto mb-3 text-emerald-500" />
                <div className="font-medium mb-1">Check your inbox</div>
                <p className="text-sm text-[var(--text-3)]">
                  We sent a one-time sign-in link to <strong>{email}</strong>. It expires in 30 minutes.
                </p>
                <Button variant="ghost" size="sm" className="mt-4" onClick={() => setSent(false)} data-testid="magic-link-resend-btn">
                  Use a different email
                </Button>
              </GlassCard>
            </motion.div>
          ) : (
            <motion.form key="form" onSubmit={submit} initial={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3" data-testid="inbox-request-form">
              <div className="relative">
                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
                <Input
                  type="email"
                  placeholder="your@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-9"
                  data-testid="inbox-email-input"
                  required
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 gap-2" data-testid="inbox-send-link-btn">
                {loading ? "Sending…" : <>Email me a sign-in link <ArrowRight size={14} /></>}
              </Button>
              <div className="text-[11px] text-[var(--text-3)] flex items-center justify-center gap-1 pt-2">
                <ShieldCheck size={11} /> No passwords. Link expires in 30 minutes.
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        <div className="mt-12 text-center text-xs text-[var(--text-3)]">
          Powered by <Link to="/" className="underline">Clariva</Link>
        </div>
      </motion.div>
    </div>
  );
}

// =========== HOME: grouped-by-tenant inbox ===========
function InboxHome({ email, onSignOut }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    inboxApi.get("/external/inbox")
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load inbox"))
      .finally(() => setLoading(false));
  }, []);

  const signOut = () => {
    inboxToken.clear();
    onSignOut?.();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[var(--text-3)]">Loading your contracts…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 bg-[var(--bg)]/80 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Brand size={32} testId="inbox-header-brand" />
            <div>
              <div className="text-sm font-medium">Signatory inbox</div>
              <div className="text-[10px] text-[var(--text-3)] overline">{email}</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Badge variant="outline" className="gap-1" data-testid="inbox-contract-count">
              <Buildings size={10} /> {data?.total_contracts || 0} contracts · {data?.groups?.length || 0} customers
            </Badge>
            <Button variant="ghost" size="sm" onClick={signOut} data-testid="inbox-signout-btn" className="gap-1.5">
              <SignOut size={14} /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {(!data?.groups || data.groups.length === 0) ? (
          <GlassCard className="p-10 text-center" data-testid="inbox-empty">
            <Envelope size={32} className="mx-auto mb-3 text-[var(--text-3)]" />
            <h2 className="text-xl font-semibold mb-1">Inbox is empty</h2>
            <p className="text-sm text-[var(--text-3)]">
              Nothing awaiting your signature right now. When a Clariva customer invites you to sign a contract, it'll appear here.
            </p>
          </GlassCard>
        ) : data.groups.map((g, gi) => (
          <TenantGroup key={g.tenant_id} group={g} index={gi} />
        ))}
      </main>
    </div>
  );
}

function statusChip(status) {
  const map = {
    awaiting_signature: { c: "bg-amber-500/15 text-amber-600", label: "Awaiting your signature", icon: PenNib },
    in_review: { c: "bg-sky-500/15 text-sky-600", label: "In review", icon: Eye },
    draft: { c: "bg-gray-500/15 text-gray-600", label: "Draft", icon: Clock },
    active: { c: "bg-emerald-500/15 text-emerald-600", label: "Active", icon: CheckCircle },
    expired: { c: "bg-rose-500/15 text-rose-600", label: "Expired", icon: Gavel },
  };
  return map[status] || { c: "bg-gray-500/10 text-gray-500", label: status || "—", icon: Clock };
}

function TenantGroup({ group, index }) {
  const brand = group.branding || {};
  const logo = (brand.logo || {}).url;
  const accent = brand.primary_color;
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="mb-10"
      data-testid={`inbox-group-${group.tenant_slug || group.tenant_id}`}
      style={accent ? { "--accent": accent } : {}}
    >
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[var(--border)]">
        {logo ? (
          <img src={logo} alt={group.tenant_name} className="w-10 h-10 rounded-md object-contain bg-[var(--surface)] border border-[var(--border)]" />
        ) : (
          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[var(--accent)] to-[var(--primary)] flex items-center justify-center">
            <Buildings size={18} weight="fill" className="text-[var(--primary-fg)]" />
          </div>
        )}
        <div>
          <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>{group.tenant_name}</div>
          <div className="text-xs text-[var(--text-3)]">{group.contracts.length} contract{group.contracts.length === 1 ? "" : "s"}</div>
        </div>
      </div>
      <div className="grid gap-3">
        {group.contracts.map(c => <InboxContractRow key={c.id} contract={c} />)}
      </div>
    </motion.section>
  );
}

function InboxContractRow({ contract }) {
  const chip = statusChip(contract.status);
  const StatusIcon = chip.icon;
  const [signing, setSigning] = useState(false);

  const preview = () => {
    const url = `${BACKEND}/api/external/contracts/${contract.id}/preview.pdf`;
    const t = inboxToken.get();
    // Open a new tab with token header via fetch + blob (since preview.pdf needs bearer auth)
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.blob())
      .then(b => { const u = URL.createObjectURL(b); window.open(u, "_blank", "noopener"); })
      .catch(() => toast.error("Could not open preview"));
  };

  const sign = async () => {
    setSigning(true);
    try {
      const { data } = await inboxApi.post(`/external/contracts/${contract.id}/sign-link`);
      window.location.href = `/share/${data.token}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open signing page");
    } finally { setSigning(false); }
  };

  return (
    <GlassCard className="p-4 flex items-center gap-4" data-testid={`inbox-contract-${contract.id}`}>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${chip.c}`}>
        <StatusIcon size={16} weight="fill" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{contract.title || "Untitled contract"}</div>
        <div className="text-xs text-[var(--text-3)] flex items-center gap-2 mt-0.5 truncate">
          <span>{contract.counterparty || "—"}</span>
          {contract.value_amount && <span>· {contract.value_currency || ""} {contract.value_amount.toLocaleString?.()}</span>}
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ${chip.c}`}>
            {chip.label}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="outline" size="sm" onClick={preview} data-testid={`inbox-preview-${contract.id}`} className="gap-1.5">
          <Eye size={13} /> Preview
        </Button>
        <Button size="sm" onClick={sign} disabled={signing} data-testid={`inbox-sign-${contract.id}`} className="gap-1.5">
          <PenNib size={13} /> {signing ? "Opening…" : "Review & Sign"} <CaretRight size={12} />
        </Button>
      </div>
    </GlassCard>
  );
}

// =========== CONSUME: handle magic-link click ===========
// Module-level dedupe so React StrictMode's double-invoke doesn't "burn" a magic link
const _consumedTokens = new Set();

function InboxConsume() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setError("Missing token"); return; }
    if (_consumedTokens.has(token)) return;  // StrictMode guard
    _consumedTokens.add(token);
    inboxApi.post("/external/consume", { token })
      .then(r => {
        inboxToken.set(r.data.access_token);
        nav("/inbox", { replace: true });
      })
      .catch(e => setError(e?.response?.data?.detail || "Invalid link"));
  }, [params, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <GlassCard className="p-8 max-w-md text-center" data-testid="inbox-consume-card">
        {error ? (
          <>
            <div className="text-lg font-medium mb-2">Sign-in link issue</div>
            <p className="text-sm text-[var(--text-3)] mb-4">{error}</p>
            <Link to="/inbox"><Button data-testid="inbox-consume-retry">Request a new link</Button></Link>
          </>
        ) : (
          <>
            <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 1.5 }}>
              <Sparkle size={32} weight="fill" className="mx-auto mb-3 text-[var(--accent)]" />
            </motion.div>
            <div className="font-medium">Signing you in…</div>
          </>
        )}
      </GlassCard>
    </div>
  );
}

// =========== ROOT ===========
export default function Inbox() {
  const { slug } = useParams();
  const [branding, setBranding] = useState(null);
  const [authenticated, setAuthenticated] = useState(!!inboxToken.get());
  const [email, setEmail] = useState(null);

  useEffect(() => {
    const host = window.location.host;
    inboxApi.get("/external/branding", { params: { slug: slug || undefined, host } })
      .then(r => setBranding(r.data))
      .catch(() => setBranding({ tenant_name: "Clariva", branding: {}, is_clariva_default: true }));
  }, [slug]);

  useEffect(() => {
    if (!authenticated) return;
    inboxApi.get("/external/me")
      .then(r => setEmail(r.data.email))
      .catch(() => { inboxToken.clear(); setAuthenticated(false); });
  }, [authenticated]);

  if (!branding) return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--text-3)]">Loading…</div>;

  if (authenticated && email) {
    return <InboxHome email={email} onSignOut={() => { setAuthenticated(false); setEmail(null); }} />;
  }
  return (
    <InboxLanding
      tenantBranding={branding.branding}
      tenantName={branding.tenant_name}
      tenantSlug={branding.slug}
    />
  );
}

export { InboxConsume };
